import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { searchCities, type CityData } from '../../utils/cityDataLocal';
import { cityNameToUrlSlug } from '../../utils/cityData';

interface CitySearchProps {
  onCitySelect: (cityName: string) => void;
  className?: string;
}

export default function CitySearch({ onCitySelect, className = '' }: CitySearchProps) {
  const { t, i18n } = useTranslation(['worldclock', 'common']);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [filteredCities, setFilteredCities] = useState<CityData[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 搜索城市
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCities([]);
      setSelectedIndex(-1);
      return;
    }

    const performSearch = async () => {
      const language = i18n.language.startsWith('zh') ? 'zh' : 'en';
      const results = await searchCities(searchTerm, language);
      setFilteredCities(results.slice(0, 10)); // 限制显示10个结果
      setSelectedIndex(-1);
    };

    performSearch();
  }, [searchTerm, i18n.language]);

  // 点击外部关闭搜索框
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsSearchOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isSearchOpen || filteredCities.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredCities.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredCities.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleCitySelect(filteredCities[selectedIndex]);
        } else if (filteredCities.length > 0) {
          handleCitySelect(filteredCities[0]);
        }
        break;
      case 'Escape':
        setIsSearchOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  const handleCitySelect = (city: CityData) => {
    // 总是使用英文名称进行URL跳转，确保URL正确生成
    onCitySelect(city.cityNameEn);
    setSearchTerm('');
    setIsSearchOpen(false);
    inputRef.current?.blur();
  };

  const getCityDisplayName = (city: CityData) => {
    return i18n.language.startsWith('zh') ? city.cityNameZh : city.cityNameEn;
  };

  const getCountryDisplayName = (city: CityData) => {
    return i18n.language.startsWith('zh') ? city.countryZh : city.countryEn;
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsSearchOpen(true)}
          placeholder={t('searchCity', { defaultValue: 'Search for a city...' })}
          className="w-full px-4 py-2 pr-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          <i className="ri-search-line text-gray-400"></i>
        </div>
      </div>

      {/* 搜索结果下拉框 */}
      {isSearchOpen && filteredCities.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
          {filteredCities.map((city, index) => (
            <div
              key={`${city.timezone}-${city.cityNameEn}`}
              className={`px-4 py-3 cursor-pointer transition-colors ${
                index === selectedIndex
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              onClick={() => handleCitySelect(city)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-lg">{city.flag}</span>
                  <div>
                    <div className="font-medium">
                      {getCityDisplayName(city)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {getCountryDisplayName(city)}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-400">
                  {city.timezone}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 无搜索结果 */}
      {isSearchOpen && searchTerm.trim() && filteredCities.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
          <div className="text-center text-gray-500 text-sm">
            <i className="ri-search-line text-2xl mb-2 block"></i>
            {t('noCitiesFound', { defaultValue: 'No cities found' })}
          </div>
        </div>
      )}
    </div>
  );
}